import React, { useCallback, useEffect } from 'react';

import {
  LabelWith3ValuesValue,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { VerticalBarChart } from '../../../common/components/Charts/VerticalBarChart';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import ValidationAlert from '../../../app/components/ValidationAlert';

import {
  DateRangeFilterApplied,
  SuperAdminDateRangeFilter,
} from './SuperAdminDateRangeFilter';
import {
  DateRangeState,
  initDefaultLastDaysRange,
  toDateEnd,
  toDateStart,
} from './SuperAdminTopCompaniesBase';

export const SuperAdminPaymentsTab: React.FC = () => {
  const spd = useSpdCore();
  const [dataByDays, setDataByDays] = React.useState<LabelWith3ValuesValue[]>(
    [],
  );
  const [dataByMonths, setDataByMonths] = React.useState<
    LabelWith3ValuesValue[]
  >([]);
  const [filters, setFilters] = React.useState<DateRangeState>({
    dateFrom: '',
    dateTo: '',
  });
  const [applied, setApplied] = React.useState<DateRangeFilterApplied>({
    dateFrom: '',
    dateTo: '',
  });
  const [errors, setErrors] = React.useState<string[]>([]);
  const [errorsMonthly, setErrorsMonthly] = React.useState<string[]>([]);
  const [filtersMonthly, setFiltersMonthly] = React.useState<DateRangeState>({
    dateFrom: '',
    dateTo: '',
  });

  const loadByDays = useCallback(
    (a: DateRangeFilterApplied) => {
      setErrors([]);
      spd.reports
        .reportPaymentsByDate(
          true,
          toDateStart(a.dateFrom),
          toDateEnd(a.dateTo),
        )
        .then(data => setDataByDays(data))
        .catch(async error => {
          setDataByDays([]);
          console.error('Error during report payments by days', error);
          setErrors(await responseErrors(error as ResponseError));
        });
    },
    [spd.reports],
  );

  const loadByMonths = useCallback(
    (a: DateRangeFilterApplied) => {
      setErrorsMonthly([]);
      spd.reports
        .reportPaymentsByDate(
          undefined,
          toDateStart(a.dateFrom),
          toDateEnd(a.dateTo),
        )
        .then(data => setDataByMonths(data))
        .catch(async error => {
          setDataByMonths([]);
          console.error('Error during report payments by months', error);
          setErrorsMonthly(await responseErrors(error as ResponseError));
        });
    },
    [spd.reports],
  );

  useEffect(() => {
    void initDefaultLastDaysRange(
      spd.params.getReportLastDays,
      range => {
        setFilters(range);
        setApplied(range);
        loadByDays(range);
      },
      async error => {
        console.error('Error during report last days param fetch', error);
        setErrors(await responseErrors(error as ResponseError));
      },
    );
  }, [spd.params, loadByDays]);

  useEffect(() => {
    loadByMonths({ dateFrom: '', dateTo: '' });
  }, [loadByMonths]);

  return (
    <>
      <SuperAdminDateRangeFilter
        filters={filters}
        setFilters={setFilters}
        onApply={a => {
          setErrors([]);
          setDataByDays([]);
          setApplied(a);
          loadByDays(a);
        }}
      />
      <ValidationAlert
        show={errors.length > 0}
        errors={errors}
        className="mt-3"
      />
      {errors.length === 0 && (
        <VerticalBarChart
          title={`Wpłaty w zakresie: ${applied.dateFrom} - ${applied.dateTo}`}
          labels={dataByDays.map(o => o.label)}
          data={dataByDays.map(o => o.value1)}
          data2={dataByDays.map(o => o.value2)}
          data3={dataByDays.map(o => o.value3)}
          datasetLabel="Wpłaty do paragonów"
          backgroundColor="rgba(22, 163, 74, 0.7)"
          borderColor="rgba(22, 163, 74, 1)"
          datasetLabel2="Wpłaty do faktur"
          backgroundColor2="rgba(37, 99, 235, 0.7)"
          borderColor2="rgba(37, 99, 235, 1)"
          datasetLabel3="Wpłaty niedowiązane"
          backgroundColor3="rgba(245, 158, 11, 0.7)"
          borderColor3="rgba(245, 158, 11, 1)"
          stacked={true}
          key="paymentsByDays"
        />
      )}
      <SuperAdminDateRangeFilter
        filters={filtersMonthly}
        setFilters={setFiltersMonthly}
        monthPicker={true}
        applyLabel="Pokaż zestawienie miesięczne"
        onApply={a => {
          setErrorsMonthly([]);
          setDataByMonths([]);
          loadByMonths(a);
        }}
      />
      <ValidationAlert
        show={errorsMonthly.length > 0}
        errors={errorsMonthly}
        className="mt-3"
      />
      {errorsMonthly.length === 0 && (
        <VerticalBarChart
          title="Wpłaty w podziale na miesiące"
          labels={dataByMonths.map(o => o.label)}
          data={dataByMonths.map(o => o.value1)}
          data2={dataByMonths.map(o => o.value2)}
          data3={dataByMonths.map(o => o.value3)}
          datasetLabel="Wpłaty do paragonów"
          backgroundColor="rgba(22, 163, 74, 0.7)"
          borderColor="rgba(22, 163, 74, 1)"
          datasetLabel2="Wpłaty do faktur"
          backgroundColor2="rgba(37, 99, 235, 0.7)"
          borderColor2="rgba(37, 99, 235, 1)"
          datasetLabel3="Wpłaty niedowiązane"
          backgroundColor3="rgba(245, 158, 11, 0.7)"
          borderColor3="rgba(245, 158, 11, 1)"
          stacked={true}
          key="paymentsByMonths"
        />
      )}
    </>
  );
};
